import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { catalogSongMessages } from './catalogSongMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the song files; each part uses its own slice of it */
import styles from './SongFiles.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The longest lyrics the server takes (music24-api `PATCH .../song`). */
export const SONG_LYRICS_MAX_LENGTH = 6000;

interface SongLyricsFieldProps {
  lyrics: string | null;
  isSaving: boolean;
  onSave: (lyrics: string | null) => Promise<boolean>;
  /** The id of the text box, so another part of the sheet can put the focus in it. */
  inputId?: string;
}

/** The lyrics of the song files sheet (design f2). An empty box clears the lyrics. */
const SongLyricsFieldComponent: React.FC<SongLyricsFieldProps> = ({ lyrics, isSaving, onSave, inputId }) => {
  const intl = useIntl();
  const [value, setValue] = useState(lyrics ?? '');
  const label = intl.formatMessage(catalogSongMessages.lyrics);

  useEffect(() => setValue(lyrics ?? ''), [lyrics]);

  const change = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => setValue(event.target.value), []);
  const save = useCallback(() => {
    const trimmed = value.trim();
    onSave(trimmed === '' ? null : trimmed).catch(console.error);
  }, [onSave, value]);

  const isChanged = value.trim() !== (lyrics ?? '').trim();

  return (
    <div className={styles.field}>
      <div className={styles.labelRow}>
        <span className={styles.label}>{label}</span>
        {isChanged && (
          <SMLButton size="sm" outline omitCap isLoading={isSaving} disabled={isSaving} onClick={save}>
            {intl.formatMessage(catalogSongMessages.lyricsSave)}
          </SMLButton>
        )}
      </div>
      <textarea
        id={inputId}
        className={styles.textarea}
        value={value}
        rows={5}
        maxLength={SONG_LYRICS_MAX_LENGTH}
        aria-label={label}
        placeholder={intl.formatMessage(catalogSongMessages.lyricsPlaceholder)}
        onChange={change}
      />
    </div>
  );
};

export const SongLyricsField = TypedMemo(SongLyricsFieldComponent);
